import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { useState } from 'react'
import { CourseCard } from '@/components/courses/CourseCard'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { type CourseWithStats, useCoursesWithStats } from '@/hooks/use-courses'

const LEVELS = ['ENTRY', 'BASIC', 'ADVANCED', 'EXPERT'] as const
const PAGE_SIZE = 9

export default function CoursesPage() {
  const [search, setSearch] = useState('')
  const [level, setLevel] = useState<string>('')
  const [page, setPage] = useState(1)

  const { data: response, isLoading } = useCoursesWithStats({
    search: search || undefined,
    level: level || undefined,
    isPublished: 'true',
    page: String(page),
    limit: String(PAGE_SIZE),
  })

  const courses: CourseWithStats[] = response?.data ?? []
  const pagination = response?.pagination

  const handleLevelChange = (newLevel: string) => {
    setLevel(newLevel)
    setPage(1)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold">コース一覧</h1>
          <p className="text-sm text-muted-foreground mt-1">
            あなたのスキルアップに役立つコースを見つけましょう
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="コースを検索..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          className="pl-9"
        />
      </div>

      {/* Level filter tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={level === '' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleLevelChange('')}
        >
          すべて
        </Button>
        {LEVELS.map((l) => (
          <Button
            key={l}
            variant={level === l ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleLevelChange(l)}
          >
            {l}
          </Button>
        ))}
      </div>

      {/* Course grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={`course-skeleton-${i}`} className="h-48" />
          ))}
        </div>
      ) : courses.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">コースが見つかりません。</p>
          {level && (
            <p className="text-sm text-muted-foreground mt-2">
              {level}レベルのコースは現在ありません。
            </p>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {courses.map((course) => (
            <CourseCard
              key={course.id}
              id={course.id}
              title={course.title}
              description={course.description}
              category={course.category}
              level={course.level}
              lessonCount={course.lessonCount}
              enrollmentCount={course.enrollmentCount}
              totalDuration={course.totalDuration}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button
            variant="outline"
            size="icon"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
            let pageNum: number
            if (pagination.totalPages <= 5) {
              pageNum = i + 1
            } else if (page <= 3) {
              pageNum = i + 1
            } else if (page >= pagination.totalPages - 2) {
              pageNum = pagination.totalPages - 4 + i
            } else {
              pageNum = page - 2 + i
            }
            if (pageNum < 1 || pageNum > pagination.totalPages) return null
            return (
              <Button
                key={pageNum}
                variant={page === pageNum ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPage(pageNum)}
              >
                {pageNum}
              </Button>
            )
          })}
          <Button
            variant="outline"
            size="icon"
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}
